/**
 * ч.1398: платформа → «Баланс і тарифи клієнтів»: баланси акаунтів, стан баз, ручне поповнення
 * (за рахунком / карткою — поки руками оператора), коригування, журнал акаунта, такт біллінгу.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { fmtBytes, fmtDate, fmtUah, INCOMING_HOW_UK, INVOICE_STATUS_UK, LEDGER_KIND_UK, platformBilling, STATUS_UK, type BillingInvoice, type IncomingPayment, type PlatformBillingRow } from '@/api/billing';
import { useConfirm } from '@/components/ConfirmProvider';
import BodyPortal from '@/shared/components/BodyPortal';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';
import TchTable from '@/shared/components/TchTable';

function errText(e: unknown): string {
  const d = (e as AxiosError<{ detail?: unknown }>)?.response?.data?.detail;
  return typeof d === 'string' ? d : 'Не вдалося виконати дію';
}

/** Рішення людини щодо надходження: на рахунок (тоді клієнт зможе сформувати акт) або просто на баланс. */
function IncomingDecisionDialog({ row, invoices, tenants, onClose, onDone }: {
  row: IncomingPayment; invoices: BillingInvoice[]; tenants: PlatformBillingRow[]; onClose: () => void; onDone: () => void;
}) {
  const [mode, setMode] = useState<'invoice' | 'tenant'>(row.suggested_invoice_id ? 'invoice' : 'tenant');
  const [invoiceId, setInvoiceId] = useState(row.suggested_invoice_id ?? '');
  const [tenantId, setTenantId] = useState(row.tenant_id ?? '');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => platformBilling.creditIncoming(row.id, mode === 'invoice'
      ? { invoice_id: invoiceId, note: note.trim() } : { tenant_id: tenantId, note: note.trim() }),
    onSuccess: onDone,
    onError: (e) => setError(errText(e)),
  });
  const chosen = mode === 'invoice' ? !!invoiceId : !!tenantId;
  return (
    <BodyPortal>
      <div role="dialog" aria-modal="true" aria-label="Зарахування надходження" onClick={onClose}
        style={{ position: 'fixed', inset: 0, zIndex: 1100, background: 'var(--overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
        <div className="card" onClick={(e) => e.stopPropagation()} style={{ width: 560, maxWidth: '100%', margin: 0 }}>
          <h3 style={{ margin: '0 0 0.4rem' }}>Зарахувати {fmtUah(row.amount)}</h3>
          <p style={{ color: 'var(--muted)', fontSize: '0.85rem', margin: '0 0 0.6rem' }}>
            {row.payer_name ?? 'Платник невідомий'}{row.payer_code ? ` (${row.payer_code})` : ''}, {fmtDate(row.receipt_date)}. {row.purpose}
          </p>
          {error && <div className="mc__panel mc__panel--warn" role="alert" style={{ marginBottom: '0.5rem' }}>{error}</div>}
          <div className="seg-tabs" style={{ marginBottom: '0.5rem' }}>
            <button className={mode === 'invoice' ? 'active' : ''} onClick={() => setMode('invoice')}>на рахунок</button>
            <button className={mode === 'tenant' ? 'active' : ''} onClick={() => setMode('tenant')}>на баланс акаунта</button>
          </div>
          {mode === 'invoice' ? (
            <div className="field">
              <label>Рахунок</label>
              <select value={invoiceId} onChange={(e) => setInvoiceId(e.target.value)} aria-label="Рахунок">
                <option value="">— оберіть —</option>
                {invoices.map((i) => (
                  <option key={i.id} value={i.id}>№ {i.number} · {i.tenant_name} · {fmtUah(i.amount)} · {INVOICE_STATUS_UK[i.status]}</option>
                ))}
              </select>
              <div style={{ color: 'var(--muted)', fontSize: '0.8rem', marginTop: 3 }}>Клієнт зможе сформувати акт на цю суму.</div>
            </div>
          ) : (
            <div className="field">
              <label>Акаунт</label>
              <select value={tenantId} onChange={(e) => setTenantId(e.target.value)} aria-label="Акаунт">
                <option value="">— оберіть —</option>
                {tenants.map((t) => <option key={t.tenant_id} value={t.tenant_id}>{t.name}</option>)}
              </select>
              <div style={{ color: 'var(--muted)', fontSize: '0.8rem', marginTop: 3 }}>Без рахунка акт не формується.</div>
            </div>
          )}
          <div className="field">
            <label>Чому так</label>
            <input value={note} onChange={(e) => setNote(e.target.value)} aria-label="Чому так" maxLength={255} />
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
            <button onClick={onClose}>скасувати</button>
            <button className="primary" disabled={!chosen || !note.trim() || save.isPending}
              title={!chosen ? 'Оберіть, куди зарахувати' : !note.trim() ? 'Вкажіть причину' : undefined}
              onClick={() => save.mutate()}>зарахувати</button>
          </div>
        </div>
      </div>
    </BodyPortal>
  );
}

export default function PlatformBillingTab() {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const q = useQuery({ queryKey: ['platform-billing'], queryFn: platformBilling.list });
  const [tenantId, setTenantId] = useState('');
  const [amount, setAmount] = useState('150');
  const [kind, setKind] = useState('TOPUP_INVOICE');
  const [note, setNote] = useState('');
  const [adjust, setAdjust] = useState('');
  const [adjustNote, setAdjustNote] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tickResult, setTickResult] = useState<string | null>(null);
  const [vendorOrg, setVendorOrg] = useState<string | null>(null);
  const [validDays, setValidDays] = useState<number | null>(null);
  const [invNote, setInvNote] = useState<string | null>(null);
  const [invFilter, setInvFilter] = useState('ISSUED');
  const [pubkey, setPubkey] = useState<string | null>(null);
  const settingsQ = useQuery({ queryKey: ['platform-billing-settings'], queryFn: platformBilling.settings });
  const invoicesQ = useQuery({ queryKey: ['platform-billing-invoices', invFilter], queryFn: () => platformBilling.invoices(invFilter || undefined) });
  const allInvoicesQ = useQuery({ queryKey: ['platform-billing-invoices', 'all-for-decision'], queryFn: () => platformBilling.invoices() });
  const st = settingsQ.data;
  const vendorValue = vendorOrg ?? st?.vendor_org_id ?? '';
  const daysValue = validDays ?? st?.invoice_valid_days ?? 7;
  const noteValue = invNote ?? st?.invoice_note ?? '';
  const pubkeyValue = pubkey ?? st?.acquiring_pubkey ?? '';
  const saveSettings = useMutation({
    mutationFn: () => platformBilling.saveSettings({ vendor_org_id: vendorValue || null, invoice_valid_days: daysValue, invoice_note: noteValue || null, acquiring_pubkey: pubkeyValue.trim() || null }),
    onSuccess: () => { setError(null); qc.invalidateQueries({ queryKey: ['platform-billing-settings'] }); },
    onError: (e) => setError(errText(e)),
  });
  const refreshMoney = () => {
    qc.invalidateQueries({ queryKey: ['platform-billing-incoming'] });
    qc.invalidateQueries({ queryKey: ['platform-billing-invoices'] });
    qc.invalidateQueries({ queryKey: ['platform-billing'] });
  };
  const markPaid = useMutation({
    mutationFn: (p: { id: string; note: string }) => platformBilling.markPaid(p.id, p.note),
    onSuccess: () => { setError(null); refreshMoney(); },
    onError: (e) => setError(errText(e)),
  });
  const cancelInv = useMutation({
    mutationFn: (id: string) => platformBilling.cancelInvoice(id),
    onSuccess: () => { setError(null); qc.invalidateQueries({ queryKey: ['platform-billing-invoices'] }); },
    onError: (e) => setError(errText(e)),
  });
  const match = useMutation({
    mutationFn: platformBilling.match,
    onSuccess: (r) => { setTickResult(`звірка: нових платежів ${r.checked}, зараховано ${r.matched}, на розбір ${r.review}`); refreshMoney(); },
    onError: (e) => setError(errText(e)),
  });
  const [incFilter, setIncFilter] = useState('REVIEW');
  const [decide, setDecide] = useState<IncomingPayment | null>(null);
  const incomingQ = useQuery({ queryKey: ['platform-billing-incoming', incFilter], queryFn: () => platformBilling.incoming(incFilter) });
  const ignoreInc = useMutation({
    mutationFn: (p: { id: string; note: string }) => platformBilling.ignoreIncoming(p.id, p.note),
    onSuccess: () => { setError(null); refreshMoney(); },
    onError: (e) => setError(errText(e)),
  });
  const reopenInc = useMutation({
    mutationFn: (id: string) => platformBilling.reopenIncoming(id),
    onSuccess: () => { setError(null); refreshMoney(); },
    onError: (e) => setError(errText(e)),
  });
  const incCols: ColumnDef<IncomingPayment>[] = [
    { key: 'receipt_date', header: 'Дата', width: 100, sortValue: (r) => r.receipt_date, cell: (r) => fmtDate(r.receipt_date) },
    { key: 'amount', header: 'Сума', width: 110, align: 'right', sortValue: (r) => Number(r.amount), cell: (r) => fmtUah(r.amount) },
    { key: 'payer', header: 'Платник', width: 190, cell: (r) => <span style={{ whiteSpace: 'normal' }}>{r.payer_name ?? '—'}{r.payer_code ? ` (${r.payer_code})` : ''}</span> },
    { key: 'purpose', header: 'Призначення', width: 260, cell: (r) => <span style={{ whiteSpace: 'normal' }}>{r.purpose ?? ''}</span> },
    { key: 'reason', header: 'Стан', width: 210, cell: (r) => (r.status === 'CREDITED' ? (
      <span>
        {r.invoice_number ? `рахунок № ${r.invoice_number}` : 'на баланс'}{r.tenant_name ? ` · ${r.tenant_name}` : ''}
        {r.how ? ` · ${INCOMING_HOW_UK[r.how] ?? r.how}` : ''}
        {r.post_error && <div style={{ color: 'var(--badge-red-fg)' }}>не проведено в обліку: {r.post_error}</div>}
      </span>
    ) : (r.reason ?? r.note ?? '')) },
    { key: 'act', header: '', width: 210, cell: (r) => (r.status === 'REVIEW' ? (
      <span style={{ display: 'inline-flex', gap: '0.4rem', flexWrap: 'wrap' }}>
        <button className="primary" onClick={() => setDecide(r)}>зарахувати</button>
        <button disabled={ignoreInc.isPending} onClick={() => {
          const note = window.prompt('Чому це надходження не від клієнта (позика, внесок, повернення)?');
          if (note?.trim()) ignoreInc.mutate({ id: r.id, note: note.trim() });
        }}>не наше</button>
      </span>
    ) : r.status === 'IGNORED' ? (
      <button disabled={reopenInc.isPending} onClick={() => reopenInc.mutate(r.id)}>повернути на розбір</button>
    ) : null) },
  ];
  const invCols: ColumnDef<BillingInvoice>[] = [
    { key: 'invoice_date', header: 'Дата', width: 100, sortValue: (r) => r.invoice_date, cell: (r) => fmtDate(r.invoice_date) },
    { key: 'number', header: 'Номер', width: 120, sortValue: (r) => r.number, cell: (r) => r.number },
    { key: 'tenant_name', header: 'Акаунт', cell: (r) => r.tenant_name ?? '', sortValue: (r) => r.tenant_name ?? '' },
    { key: 'payer', header: 'Платник', cell: (r) => `${r.payer_name}${r.payer_code ? ` (${r.payer_code})` : ''}` },
    { key: 'amount', header: 'Сума', width: 110, align: 'right', sortValue: (r) => Number(r.amount), cell: (r) => fmtUah(r.amount) },
    { key: 'status', header: 'Стан', width: 150, cell: (r) => `${INVOICE_STATUS_UK[r.status]}${r.paid_at ? ` ${fmtDate(r.paid_at)}` : ''}` },
    { key: 'note', header: 'Примітка', cell: (r) => r.note ?? '' },
    { key: 'act', header: '', width: 200, cell: (r) => r.status !== 'ISSUED' ? null : (
      <span style={{ display: 'inline-flex', gap: '0.4rem' }}>
        <button disabled={markPaid.isPending} onClick={async () => {
          if (await confirm({ title: 'Оплату отримано', message: `Зарахувати ${fmtUah(r.amount)} акаунту «${r.tenant_name}» за рахунком № ${r.number}?`, okLabel: 'зарахувати' })) {
            markPaid.mutate({ id: r.id, note: 'підтверджено вручну' });
          }
        }}>оплачено</button>
        <button disabled={cancelInv.isPending} onClick={async () => {
          if (await confirm({ title: 'Скасувати рахунок', message: `Скасувати рахунок № ${r.number}?`, okLabel: 'скасувати', danger: true })) cancelInv.mutate(r.id);
        }}>скасувати</button>
      </span>
    ) },
  ];
  const detailQ = useQuery({ queryKey: ['platform-billing-detail', open], queryFn: () => platformBilling.detail(open!), enabled: !!open });
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['platform-billing'] });
    qc.invalidateQueries({ queryKey: ['platform-billing-detail'] });
  };
  const topup = useMutation({
    mutationFn: () => platformBilling.topup({ tenant_id: tenantId, amount: Number(amount), kind, note: note || null }),
    onSuccess: () => { setError(null); setNote(''); refresh(); },
    onError: (e) => setError(errText(e)),
  });
  const adjustM = useMutation({
    mutationFn: () => platformBilling.adjust({ tenant_id: tenantId, amount: Number(adjust), note: adjustNote }),
    onSuccess: () => { setError(null); setAdjust(''); setAdjustNote(''); refresh(); },
    onError: (e) => setError(errText(e)),
  });
  const tick = useMutation({
    mutationFn: platformBilling.tick,
    onSuccess: (r) => { setTickResult(Object.entries(r).map(([k, v]) => `${k}: ${v}`).join(', ') || 'нічого не змінилось'); refresh(); },
    onError: (e) => setError(errText(e)),
  });
  const d = q.data;
  const t = d?.tariffs;

  const cols: ColumnDef<PlatformBillingRow>[] = [
    { key: 'name', header: 'Акаунт', sortValue: (r) => r.name,
      cell: (r) => <span>{r.name}{r.is_tester && <span style={{ color: 'var(--muted)' }}> · тестовий</span>}</span> },
    { key: 'owners', header: 'Власники', cell: (r) => r.owners ?? '—' },
    { key: 'balance', header: 'Баланс', width: 120, align: 'right', sortValue: (r) => Number(r.balance), cell: (r) => fmtUah(r.balance) },
    { key: 'bases', header: 'Баз', width: 70, align: 'right', sortValue: (r) => r.bases, cell: (r) => r.bases },
    { key: 'active', header: 'Оплачено', width: 90, align: 'right', cell: (r) => r.active },
    { key: 'trial', header: 'Пробних', width: 90, align: 'right', cell: (r) => r.trial },
    { key: 'unpaid', header: 'Не сплачено', width: 110, align: 'right', sortValue: (r) => r.unpaid,
      cell: (r) => r.unpaid ? <b style={{ color: 'var(--badge-red-fg)' }}>{r.unpaid}</b> : 0 },
    { key: 'next_due', header: 'Найближча оплата', width: 150, sortValue: (r) => r.next_due ?? '', cell: (r) => fmtDate(r.next_due) },
    { key: 'gift_month', header: 'Подарунок', width: 110, cell: (r) => r.gift_month ? r.gift_month.slice(0, 7) : '—' },
    { key: 'act', header: '', width: 90, cell: (r) => <button onClick={() => setOpen(r.tenant_id)}>деталі</button> },
  ];
  const det = detailQ.data;

  return (
    <div>
      {t && (
        <p style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
          База {fmtUah(t.base_price)} / {t.period_days} дн. ({t.base_quota_mb} МБ); +{t.extra_block_mb} МБ — {fmtUah(t.extra_block_price)};
          кредити {Number(t.credits_pack).toFixed(0)} за {fmtUah(t.credits_pack_price)}; подарунок {Number(t.gift_credits).toFixed(0)} кредитів/міс після оплати;
          пробний період {t.trial_days} дн., лише перегляд {t.grace_days} дн., видалення через {t.purge_days} дн. Ціни без ПДВ.
        </p>
      )}
      {error && <div className="mc__panel mc__panel--warn" role="alert" style={{ marginBottom: '0.6rem' }}>{error}</div>}
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
        <div className="field" style={{ minWidth: 260 }}>
          <label>Акаунт</label>
          <select value={tenantId} onChange={(e) => setTenantId(e.target.value)}>
            <option value="">—</option>
            {(d?.items ?? []).map((r) => <option key={r.tenant_id} value={r.tenant_id}>{r.name} ({fmtUah(r.balance)})</option>)}
          </select>
        </div>
        <div className="field" style={{ width: 120 }}>
          <label>Сума, грн</label>
          <input type="number" step="0.01" min="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </div>
        <div className="field" style={{ width: 190 }}>
          <label>Джерело</label>
          <select value={kind} onChange={(e) => setKind(e.target.value)}>
            <option value="TOPUP_INVOICE">оплата за рахунком</option>
            <option value="TOPUP_CARD">оплата карткою</option>
            <option value="TOPUP_MANUAL">вручну / бонус</option>
          </select>
        </div>
        <div className="field" style={{ minWidth: 220 }}>
          <label>Примітка</label>
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="платіжка №… від …" />
        </div>
        <button className="primary" disabled={!tenantId || Number(amount) <= 0 || topup.isPending}
          onClick={async () => {
            const name = d?.items.find((r) => r.tenant_id === tenantId)?.name ?? '';
            if (await confirm({ title: 'Поповнення балансу', message: `Зарахувати ${fmtUah(amount)} акаунту «${name}»?`, okLabel: 'зарахувати' })) topup.mutate();
          }}>поповнити</button>
      </div>
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
        <div className="field" style={{ width: 140 }}>
          <label>Коригування, ±грн</label>
          <input type="number" step="0.01" value={adjust} onChange={(e) => setAdjust(e.target.value)} />
        </div>
        <div className="field" style={{ minWidth: 260 }}>
          <label>Причина (обовʼязково)</label>
          <input value={adjustNote} onChange={(e) => setAdjustNote(e.target.value)} />
        </div>
        <button disabled={!tenantId || !adjust || Number(adjust) === 0 || !adjustNote || adjustM.isPending}
          onClick={() => adjustM.mutate()}>скоригувати</button>
        <span style={{ flex: 1 }} />
        <button disabled={tick.isPending} title="Попередження, списання, лише перегляд → блокування → видалення (щогодини сам)"
          onClick={() => tick.mutate()}>виконати такт зараз</button>
        {tickResult && <span style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>{tickResult}</span>}
      </div>
      <DataTable<PlatformBillingRow> rows={d?.items ?? []} columns={cols} rowKey={(r) => r.tenant_id}
        loading={q.isLoading} emptyText="Акаунтів немає." initialSortKey="name" />

      <h4 style={{ margin: '1rem 0 0.4rem' }}>Продавець послуги і рахунки</h4>
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
        <div className="field" style={{ minWidth: 320 }}>
          <label>Організація-продавець (її база виписує рахунки, її виписка звіряється)</label>
          <select value={vendorValue} onChange={(e) => setVendorOrg(e.target.value)} aria-label="Організація-продавець">
            <option value="">— не налаштовано —</option>
            {(st?.organizations ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.tenant_name} · {o.name}{o.code ? ` (${o.code})` : ''}{o.has_iban ? '' : ' — без IBAN у гривні'}</option>
            ))}
          </select>
        </div>
        <div className="field" style={{ width: 130 }}>
          <label>Рахунок дійсний, дн.</label>
          <input type="number" min={1} max={60} value={daysValue} onChange={(e) => setValidDays(Number(e.target.value) || 7)} />
        </div>
        <div className="field" style={{ minWidth: 260 }}>
          <label>Примітка у рахунку</label>
          <input value={noteValue} onChange={(e) => setInvNote(e.target.value)} />
        </div>
        <div className="field" style={{ minWidth: 320 }}>
          <label title="Base64 PEM з GET /api/merchant/pubkey; порожнє — ключ береться з API mono за токеном">
            Відкритий ключ mono (вебхук) · еквайринг {st?.acquiring_enabled ? 'увімкнено' : 'вимкнено (MONO_ACQUIRING_TOKEN у .env)'}
          </label>
          <input value={pubkeyValue} onChange={(e) => setPubkey(e.target.value)} placeholder="необовʼязково" />
        </div>
        <button className="primary" disabled={saveSettings.isPending} onClick={() => saveSettings.mutate()}>зберегти</button>
        {st && !st.vendor_owner_id && st.vendor_org_id && (
          <span style={{ color: 'var(--badge-red-fg)', fontSize: '0.85rem' }}>в акаунта продавця немає власника — рахунки не випишуться</span>
        )}
      </div>
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.4rem' }}>
        <select value={invFilter} onChange={(e) => setInvFilter(e.target.value)} aria-label="Стан рахунків">
          <option value="ISSUED">очікують оплати</option>
          <option value="PAID">оплачені</option>
          <option value="CANCELLED">скасовані</option>
          <option value="">усі</option>
        </select>
        <button disabled={match.isPending} title="Відкриті рахунки × надходження у виписці продавця (у такті — щогодини)"
          onClick={() => match.mutate()}>звірити з випискою зараз</button>
      </div>
      <DataTable<BillingInvoice> rows={invoicesQ.data?.items ?? []} columns={invCols} rowKey={(r) => r.id}
        loading={invoicesQ.isLoading} emptyText="Рахунків немає." initialSortKey="invoice_date" initialSortDir="desc" />

      <h3 style={{ fontSize: '0.95rem', margin: '1.25rem 0 0.3rem' }}>Надходження на рахунок продавця</h3>
      <p style={{ color: 'var(--muted)', fontSize: '0.85rem', margin: '0 0 0.4rem' }}>
        Автоматично зараховується лише те, що збіглось за двома ознаками: номер рахунка, код платника, сума. Решта чекає тут.
        {(incomingQ.data?.acts_to_sign ?? 0) > 0 && (
          <b> Актів на підпис: {incomingQ.data?.acts_to_sign} (журнал ЕДО організації-продавця, вхідні, «підписати пачкою»).</b>
        )}
        {(incomingQ.data?.acts_not_posted ?? 0) > 0 && (
          <span style={{ color: 'var(--badge-red-fg)' }}> Підписані акти, що не провелись в обліку: {incomingQ.data?.acts_not_posted}.</span>
        )}
      </p>
      <div className="seg-tabs" style={{ marginBottom: '0.5rem' }}>
        {([['REVIEW', 'на розборі'], ['CREDITED', 'зараховані'], ['IGNORED', 'не наші']] as const).map(([k, label]) => (
          <button key={k} className={incFilter === k ? 'active' : ''} onClick={() => setIncFilter(k)}>
            {label}{incomingQ.data?.counts[k] ? ` (${incomingQ.data.counts[k]})` : ''}
          </button>
        ))}
      </div>
      <DataTable<IncomingPayment> rows={incomingQ.data?.items ?? []} columns={incCols} rowKey={(r) => r.id}
        loading={incomingQ.isLoading} emptyText="Надходжень немає." initialSortKey="receipt_date" initialSortDir="desc" />
      {decide && (
        <IncomingDecisionDialog row={decide} invoices={allInvoicesQ.data?.items ?? []} tenants={d?.items ?? []}
          onClose={() => setDecide(null)} onDone={() => { setDecide(null); refreshMoney(); }} />
      )}

      {open && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1100, background: 'var(--overlay)', display: 'flex',
          alignItems: 'center', justifyContent: 'center' }} onClick={() => setOpen(null)}>
          <div className="card" style={{ width: 960, maxWidth: '95vw', maxHeight: '85vh', overflowY: 'auto', margin: 0 }}
            onClick={(e) => e.stopPropagation()}>
            <div className="toolbar">
              <h3 style={{ margin: 0 }}>{d?.items.find((r) => r.tenant_id === open)?.name} · баланс {det ? fmtUah(det.balance) : '…'}</h3>
              <button style={{ marginLeft: 'auto' }} onClick={() => setOpen(null)}>закрити</button>
            </div>
            <h4 style={{ margin: '0.4rem 0' }}>Бази</h4>
            <TchTable>
              <thead><tr><th>Організація</th><th>План</th><th>Стан</th><th>Оплачено до</th><th>Лише перегляд до</th><th>Видалення</th><th>Сховище</th><th>Тариф</th></tr></thead>
              <tbody>
                {(det?.subscriptions ?? []).map((s) => (
                  <tr key={s.organization_id}>
                    <td>{s.name}</td><td>{s.plan}</td><td>{STATUS_UK[s.status as keyof typeof STATUS_UK] ?? s.status}</td>
                    <td>{fmtDate(s.paid_until)}</td><td>{fmtDate(s.grace_until)}</td><td>{fmtDate(s.delete_after)}</td>
                    <td>{fmtBytes(s.used_bytes)}{s.extra_blocks ? ` (+${s.extra_blocks} бл.)` : ''}</td>
                    <td style={{ textAlign: 'right' }}>{fmtUah(s.monthly_price)}</td>
                  </tr>
                ))}
              </tbody>
            </TchTable>
            <h4 style={{ margin: '0.8rem 0 0.4rem' }}>Рух коштів</h4>
            <TchTable>
              <thead><tr><th>Дата</th><th>Операція</th><th>Організація</th><th>Період</th><th style={{ textAlign: 'right' }}>Сума</th><th style={{ textAlign: 'right' }}>Залишок</th><th>Примітка</th></tr></thead>
              <tbody>
                {(det?.ledger ?? []).map((l) => (
                  <tr key={l.id}>
                    <td>{new Date(l.created_at).toLocaleString('uk-UA')}</td><td>{LEDGER_KIND_UK[l.kind] ?? l.kind}</td>
                    <td>{l.organization ?? ''}</td><td>{l.period_from ? `${fmtDate(l.period_from)} – ${fmtDate(l.period_to)}` : ''}</td>
                    <td style={{ textAlign: 'right', color: Number(l.amount) < 0 ? 'var(--badge-red-fg)' : 'var(--badge-green-fg)' }}>{fmtUah(l.amount)}</td>
                    <td style={{ textAlign: 'right' }}>{fmtUah(l.balance_after)}</td><td>{l.note ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </TchTable>
          </div>
        </div>
      )}
    </div>
  );
}
